class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
    this.predictiveSearchContainer = this.querySelector(".predictive-search");

    this.calcHeight();
    this.setupEventListeners();
  }

  calcHeight() {
    this.results = this.querySelector(".predictive-search__result-group");
    this.resultsTabs = this.querySelector(".predictive-search__result-tabs");
    if (this.results) {
      if (this.resultsTabs) {
        this.results.style.setProperty(
          "--search-height",
          `${parseInt(this.resultsTabs.getBoundingClientRect().bottom)}px`
        );
      } else {
        this.results.style.removeProperty("--search-height");
      }

      const resultsWrapper = this.results.querySelector(
        ".predictive-search-results-wrapper"
      );
      if (resultsWrapper) {
        resultsWrapper.classList.toggle(
          "predictive-search-results-wrapper--scroll",
          resultsWrapper.scrollHeight > resultsWrapper.clientHeight
        );
      }
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a') ||
      event.submitter.classList.contains("card__link")
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    if (!this.classList.contains("search-wrapper")) {
      setTimeout(() => {
        if (!this.contains(document.activeElement)) this.close(true);
      });
    }
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "Enter":
        event.preventDefault();
        this.selectOption();
        break;
      case "Escape":
        this.close(true);
        this.closeResults(true);
        break;
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (
        currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
      ) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        this.renderSearchResults(resultsMarkup);
        try {
          colorSwatches();
        } catch (err) {}
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);

    // The loading state is a panel-height box too, and this is the moment the
    // panel becomes visible. Measure it now rather than inherit whatever the
    // last query left behind.
    this.calcSearchModalTop();
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
    this.clickSearchTabs();
    // The result set just changed. Nothing measured from the previous one is
    // allowed to survive this line - see refreshModalMetrics().
    this.refreshModalMetrics();
  }

  /*
   * The modal's measured geometry lives in two inline custom properties,
   * --search-modal-top and --tabs-height, and every panel height in
   * component-predictive-search.css is arithmetic on them. Both are measurements
   * of THE CURRENT RESULT SET IN THE CURRENT OPEN STATE, so both are written by
   * this element and only this element, both are refreshed together by
   * refreshModalMetrics() on every result-set change, and both are REMOVED the
   * moment the state they describe stops existing. A value can never outlive the
   * state it was measured in, which is what let a panel sized for a full result
   * set survive a shrink to zero results and paint a blank sheet over the page.
   */

  /**
   * True only when the panel is actually painted. Mirrors the visibility rules
   * in base.css: predictive-search[open]/[loading] inside an open header-search,
   * or a .predictive-initially-opened header-search showing banner content.
   */
  isModalVisible() {
    if (this.hasAttribute("open") || this.hasAttribute("loading")) return true;

    const initiallyOpened = this.closest(
      "header-search.predictive-initially-opened"
    );
    return Boolean(initiallyOpened && initiallyOpened.hasAttribute("open"));
  }

  /**
   * The single authoritative recompute. Every render path funnels through
   * renderSearchResults() into here, so a result set of any size - including
   * zero - re-derives every height from what is on screen right now.
   */
  refreshModalMetrics() {
    this.calcTabsHeight();
    this.calcSearchModalTop();
    this.calcHeight();
  }

  calcTabsHeight() {
    const tabs = this.querySelector(".predictive-search__result-tabs");

    if (!tabs) {
      // A 0-result render has no tabs. Without this removal the previous, larger
      // result set's tab height keeps being subtracted from the panel height.
      this.style.removeProperty("--tabs-height");
      return;
    }

    const tabsHeight = tabs.getBoundingClientRect().height;
    this.style.setProperty("--tabs-height", `${tabsHeight}px`);
  }

  calcSearchModalTop() {
    if (!this.predictiveSearchContainer) return;

    // --search-modal-top is the panel's top edge in VIEWPORT coordinates, and
    // the stylesheet only ever uses it as `100dvh - top`. That is meaningful
    // exclusively while the panel is on screen: measured on a closed panel whose
    // header has scrolled away, getBoundingClientRect().top is large-negative
    // and 100dvh - (-7778px) sizes the modal to 8622px. So there is no value
    // when there is no visible panel.
    if (!this.isModalVisible()) {
      this.style.removeProperty("--search-modal-top");
      return;
    }

    const clientY = this.predictiveSearchContainer.getBoundingClientRect().top;

    // A painted panel starts somewhere between the top of the viewport and its
    // bottom edge; anything outside that is scroll contamination, not geometry.
    this.style.setProperty(
      "--search-modal-top",
      `${Math.min(Math.max(clientY, 0), window.innerHeight)}px`
    );
  }

  clickSearchTabs() {
    const results = this.querySelectorAll(".predictive-search__results-list");

    if (results.length != 0) {
      results[0].classList.add("active");
      this.querySelector(".predictive-search__result-tab").classList.add(
        "active"
      );
      results[0].parentElement.classList.add("active");

      this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
        tab.addEventListener("click", (event) => {
          event.preventDefault();

          const typeTarget = tab.dataset.typeTarget;

          this.querySelectorAll(".predictive-search__result-tab").forEach(
            (element) => {
              element.classList.remove("active");
            }
          );

          tab.classList.add("active");

          results.forEach((element) => {
            let resultsType = element.dataset.type;

            if (resultsType == typeTarget) {
              element.classList.add("active");
              element.parentElement.classList.add("active");
            } else {
              element.classList.remove("active");
              element.parentElement.classList.remove("active");
            }
          });
        });
      });
    }
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  open() {
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      if (this.resetBtn) this.resetBtn.removeAttribute("style");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.predictiveSearchResults.removeAttribute("style");

    // Closed: there is no panel on screen, so there is no measured geometry.
    // This is what stops a height derived from a previous result set governing
    // the next time the modal opens.
    this.style.removeProperty("--search-modal-top");
    this.style.removeProperty("--tabs-height");
  }
}

customElements.define("predictive-search", PredictiveSearch);
